import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  MessageSquare, 
  Search,
  Clock,
  Users,
  Plus,
  Filter,
  ChevronRight,
  BookOpen
} from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ToolboxTalksPage = () => {
  const { user } = useAuth();
  const [talks, setTalks] = useState([]);
  const [categories, setCategories] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [talksRes, catsRes, statsRes] = await Promise.all([
        axios.get(`${API}/toolbox-talks`, { withCredentials: true }),
        axios.get(`${API}/toolbox-talks/categories`, { withCredentials: true }),
        axios.get(`${API}/toolbox-talk-stats`, { withCredentials: true })
      ]);
      setTalks(talksRes.data);
      setCategories(catsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load toolbox talks");
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      safety: 'bg-emerald-100 text-emerald-700',
      health: 'bg-blue-100 text-blue-700',
      equipment: 'bg-violet-100 text-violet-700',
      environment: 'bg-green-100 text-green-700',
      emergency: 'bg-rose-100 text-rose-700',
      ppe: 'bg-amber-100 text-amber-700',
      hazards: 'bg-orange-100 text-orange-700',
      procedures: 'bg-slate-100 text-slate-700',
      chemical: 'bg-purple-100 text-purple-700',
      electrical: 'bg-yellow-100 text-yellow-700',
      ergonomics: 'bg-teal-100 text-teal-700',
      fire: 'bg-red-100 text-red-700',
      fall: 'bg-indigo-100 text-indigo-700',
      confined: 'bg-gray-100 text-gray-700',
      driving: 'bg-cyan-100 text-cyan-700'
    };
    return colors[category] || 'bg-slate-100 text-slate-700';
  };

  const filteredTalks = talks.filter(talk => {
    const matchesSearch = talk.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         talk.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || talk.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="toolbox-talks-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl lg:text-3xl font-bold text-primary">
              Toolbox Talks
            </h1>
            <p className="text-muted-foreground mt-1">
              {stats?.total_talks || 0} talks available • {stats?.system_talks || 0} pre-built
            </p>
          </div>
          {(user?.role === 'super_admin' || user?.role === 'company_admin') && (
            <div className="flex gap-3">
              <Link to="/toolbox-talks/create">
                <Button data-testid="create-talk-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Talk
                </Button>
              </Link>
              <Link to="/toolbox-talks/assignments">
                <Button variant="outline" data-testid="assignments-btn">
                  <Users className="w-4 h-4 mr-2" />
                  Assignments
                </Button>
              </Link>
              <Link to="/user-groups">
                <Button variant="outline" data-testid="groups-btn">
                  <Users className="w-4 h-4 mr-2" />
                  Groups
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Stats */}
        {stats && (user?.role === 'super_admin' || user?.role === 'company_admin') && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="stat-card">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Total Talks</p>
                <p className="font-heading text-2xl font-bold text-primary">{stats.total_talks}</p>
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Assignments</p>
                <p className="font-heading text-2xl font-bold text-blue-600">{stats.total_assignments}</p>
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Completions</p>
                <p className="font-heading text-2xl font-bold text-emerald-600">{stats.total_completions}</p>
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Completion Rate</p>
                <p className="font-heading text-2xl font-bold text-amber-600">{stats.completion_rate}%</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Search & Filter */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search toolbox talks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-talks"
            />
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-48" data-testid="category-filter">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {Object.entries(categories).map(([key, name]) => (
                <SelectItem key={key} value={key}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Talks Grid */}
        {filteredTalks.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTalks.map((talk) => (
              <Card key={talk.talk_id} className="card-hover" data-testid={`talk-${talk.talk_id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <Badge className={getCategoryColor(talk.category)}>
                      {categories[talk.category] || talk.category}
                    </Badge>
                    {talk.is_system && (
                      <Badge variant="outline" className="text-xs">Pre-built</Badge>
                    )}
                  </div>
                  <h3 className="font-heading font-semibold text-primary mb-2 line-clamp-2">
                    {talk.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {talk.description}
                  </p>
                  <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{talk.duration_minutes} min</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <BookOpen className="w-4 h-4" />
                      <span>{talk.key_points?.length || 0} key points</span>
                    </div>
                  </div>
                  <Link to={`/toolbox-talks/${talk.talk_id}`}>
                    <Button variant="outline" className="w-full" data-testid={`view-talk-${talk.talk_id}`}>
                      View Talk
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="font-heading text-lg font-semibold text-primary mb-2">
                No Toolbox Talks Found
              </h3>
              <p className="text-muted-foreground">
                {searchQuery || selectedCategory !== 'all' 
                  ? 'Try adjusting your search or filters'
                  : 'No toolbox talks available yet'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ToolboxTalksPage;
